<template>
    <div class="availability-checker-widget">
        <div class="sticky-availability-form">
            <div
                :class="{
                    'booking-daily': rental === 'short',
                    whitelabel: $isWhitelabel,
                }"
                class="location-availability"
            >
                <div class="location-availability-inner">
                    <incomplete-headers
                        v-if="!isComplete"
                        :owns-space="ownsSpace"
                        :space-id="space.id"
                    />
                    <div v-else class="form-container">
                        <unavailable-cta v-if="unavailableMessage">
                            {{ unavailableMessage }}
                        </unavailable-cta>

                        <template v-if="space.waiting_list && !space.enabled">
                            <space-pricing
                                :space="space"
                            />
                            <waiting-list-form-widget
                                :space-id="space.id"
                            />
                        </template>

                        <form v-else @submit.prevent="goToBooking">
                            <default-checker
                                v-if="!isPayByPhone"
                                :states="states"
                                :space="space"
                            />
                            <pay-by-phone
                                v-else-if="space.approved"
                                :can-prebook="space.allow_phone_prebookings"
                                :can-pastbook="space.post_pay"
                                :space="space"
                            />

                            <transition name="slide-in-left" mode="out-in">
                                <div v-if="isSeason && seasonIntervalData">
                                    <season-ticket-upsell/>
                                </div>
                            </transition>

                            <transition name="slide-in-left" mode="out-in">
                                <div v-if="states.errors.length && isPayByPhone" class="text-danger">
                                    <p v-for="(error, index) in states.errors" :key="index" class="error">
                                        <i class="fa fa-exclamation-triangle" />
                                        {{ error }}
                                    </p>
                                </div>
                            </transition>

                            <availability-submit
                                v-if="space.approved"
                                :quote="quote"
                                :start-date="start_date"
                                :available-from="availableFrom"
                                :lt-available-from="space.lt_available_from"
                                :space-enabled="space.enabled"
                                :space-dummy="space.dummy"
                                :space-waiting="space.waiting_list"
                                :loading="states.loading"
                            />
                        </form>

                        <awaiting-approval-form-footer v-if="!space.approved" :owns-space="ownsSpace" :is-removed="isRemoved" :space-id="space.id" />
                        <form-footer v-else-if="!$isPartner && !$whitelabel" :space-id="space.id" :rental="rental" />
                    </div>

                    <trustpilot-widget
                        v-if="!$isWhitelabel && !isMicrosite"
                        theme="light"
                        class="hidden-xs hidden-sm"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from '../../../modules/moment';
    import SaneDates from '../../mixins/SaneDates.vue';
    import SpaceRepository from '../../../repositories/SpaceRepository';
    import { calculateDateDefaults } from '../../../modules/dates';
    import SpaceValidation from '../../mixins/SpaceValidation.vue';
    import WaitingListFormWidget from '../WaitingListFormWidget.vue';
    import Microsite from '../../mixins/Microsite';
    import TrustpilotWidget from '../../TrustpilotWidget';
    import PayByPhone from './PayByPhoneChecker';
    import IncompleteHeaders from './incomplete/Headers';
    import AvailabilitySubmit from './AvailabilitySubmit';
    import FormFooter from './FormFooter';
    import AwaitingApprovalFormFooter from './awaiting-approval/FormFooter';
    import DefaultChecker from './DefaultChecker';
    import SeasonTicketUpsell from './season-tickets/SeasonTicketUpsell';
    import UnavailableCta from './UnavailableCta';
    import SpacePricing from './SpacePricing';
    import { mapGetters } from 'vuex';
    import { mapReducers } from '../../../modules/reducer';
    import { googleLocationViewEvent } from '../../../modules/google-analytics';

    export default {
        name: 'availability-checker',

        components: {
            SpacePricing,
            UnavailableCta,
            SeasonTicketUpsell,
            TrustpilotWidget,
            WaitingListFormWidget,
            PayByPhone,
            IncompleteHeaders,
            AvailabilitySubmit,
            FormFooter,
            AwaitingApprovalFormFooter,
            DefaultChecker,
        },

        filters: {
            parseDate(value) {
                return moment(value).format('DD-MM-YYYY');
            },
        },

        mixins: [SaneDates, SpaceValidation, Microsite],

        props: {
            space: {
                type: Object,
                default() {
                    return {};
                },
            },

            landlordId: {
                type: [Number, String],
                default() {
                    return null;
                },
            },
        },

        data() {
            return {
                states: {
                    loading: false,
                    errors: [],
                },
                availableFrom: null,
            };
        },

        computed: {
            ...mapGetters('booking', [
                'seasonIntervalData',
                'seasonUpsellObject',
                'isSeason',
            ]),

            ...mapReducers('$store.state.booking.reservation_details', 'booking/updateReservationData', [
                'bookings',
                'booking_type',
                'interval_count',
                'rental',
            ]),

            /**
             * Creates proxy values of the booking dates for SaneDates and Momentiser
             */
            ...['start_date', 'start_time', 'end_date', 'end_time', 'quote'].reduce((obj, key) => ({
                ...obj,
                [key]: {
                    get() {
                        return this.bookings[0][key];
                    },
                    set(val) {
                        return this.bookings[0][key] = val;
                    },
                },
            }), {}),

            /**
             * is this booking pay by phone
             *
             * @returns {boolean}
             */
            isPayByPhone() {
                return window.location.search.includes('payByPhone');
            },

            /**
             * is this space allowed to make post pay bookings
             *
             * @returns {boolean}
             */
            isPostPay() {
                return this.space.post_pay.allow;
            },

            /**
             * is this space completed?
             *
             * @returns {boolean}
             */
            isComplete() {
                return this.space.listing_completed_at;
            },

            /**
             * has the space been removed from search
             *
             * @returns {boolean}
             */
            isRemoved() {
                return this.space.follow_up && !this.space.approved;
            },

            /**
             * is this the space's owner making a booking
             *
             * @returns {boolean}
             */
            ownsSpace() {
                return this.landlordId === this.$root.user.id;
            },

            /**
             * gets appropriate message if space is unavailable for some reason
             * @returns {boolean|string}
             */
            unavailableMessage() {
                if (!this.space.approved) {
                    return false;
                }

                if (!this.space.enabled) {
                    return 'This space is currently unavailable.';
                }

                const ltAvailableFrom = this.space.lt_available_from | this.parseDate;

                if (this.space.waiting_list && ltAvailableFrom == 0) {
                    return 'This space is currently booked.';
                }

                if (this.space.waiting_list) {
                    return `This space is currently booked until ${ ltAvailableFrom }`;
                }

                return false;
            },
        },

        watch: {
            interval_count() {
                this.getQuote();
            },

            rental() {
                calculateDateDefaults(this);
                this.getQuote();
            },

            start_date() {
                if (!this.isPayByPhone && !this.isPostPay) {
                    this.constrainStartTime();
                    this.constrainEndDateTime();
                }

                this.getQuote();
            },

            ...['end_date', 'end_time', 'start_time'].reduce((watchers, prop) => ({
                ...watchers,
                [prop]() {
                    if (!this.isPayByPhone) {
                        this.constrainEndDateTime();
                    }

                    this.getQuote();
                },
            }), {}),
        },

        created() {
            this.$store.commit('booking/setSpace', this.space);
        },

        mounted() {
            if (this.isPayByPhone || this.isPostPay) {
                this.rental = 'short';
            }

            if (!this.isPayByPhone && this.rental !== "long") {
                calculateDateDefaults(this);
            }

            this.$nextTick(() => {
                if (!this.rental || !this.space.rental_types[this.rental]) {
                    this.rental = 'short';

                    if (this.space.rental_types.long && !this.isPayByPhone) {
                        this.rental = 'long';
                    }
                }

                this.getQuote()
                    .then(() => {
                        this.logGoogleLocationView();
                    });
            });
        },

        methods: {
            /**
             * Get a quote for the current space
             */
            getQuote() {
                this.states.loading = 'Fetching quote...';
                this.states.errors = [];
                this.quote.value = null;
                this.availableFrom = null;
                const bookNow = (this.isPayByPhone && (this.booking_type === 'now'));

                const quoteData = {
                    id: this.space.id,
                    start: new moment(`${ this.start_date } ${ this.start_time }`, 'DD-MM-YYYY HH:mm').format(),
                    book_now: bookNow,
                };

                if (this.rental === 'short') {
                    quoteData.end = new moment(`${ this.end_date } ${ this.end_time }`, 'DD-MM-YYYY HH:mm').format();
                }

                // Season tickets
                if (this.rental === 'long' && this.interval_count) {
                    quoteData.interval_count = this.interval_count;
                }

                return SpaceRepository.getQuote(quoteData)
                    .then(({ data }) => {
                        this.states.loading = false;
                        this.states.errors = [];
                        this.quote = data.data;
                    })
                    .catch(({ data }) => {
                        if (!this.states.errors.includes(data.message)) {
                            this.states.errors.push(data.message);
                        }

                        this.states.loading = false;
                        if (data.additional_data) {
                            this.availableFrom = new moment(data.additional_data.available_from, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY');
                        }
                    });
            },

            goToBooking() {
                if (this.availableFrom) {
                    this.start_date = this.availableFrom;
                }

                const queries = {
                    spaceId: this.space.id,
                };

                if (this.isPayByPhone) {
                    this.rental = 'short';

                    ['start_date', 'end_date', 'start_time', 'end_time', 'rental', 'booking_type'].forEach((item) => {
                        try {
                            window.localStorage.setItem(`search-${ item }`, this[item]);
                        } catch (e) {
                            // @todo: look into cookies / polyfills for storing this data
                        }
                    });

                    queries.payByPhone = true;
                    queries.bookingType = this.booking_type;
                }

                const startDateTime = new moment(`${ this.start_date } ${ this.start_time }`, 'DD-MM-YYYY HH:mm').format();
                const currentDateTime = new moment().format();

                if (this.isPostPay && (startDateTime < currentDateTime && !this.isPayByPhone) ) {
                    this.booking_type = 'past';

                    ['start_date', 'end_date', 'start_time', 'end_time', 'rental', 'booking_type'].forEach((item) => {
                        try {
                            window.localStorage.setItem(`search-${ item }`, this[item]);
                        } catch (e) {
                            // @todo: look into cookies / polyfills for storing this data
                        }
                    });
                }

                this.getQuote()
                    .then(() => {
                        this.states.loading = 'Setting up your booking...';
                        window.location = this.$laroute.route('space.book', queries);
                        setTimeout(() => {
                            this.states.loading = false;
                        }, 500);
                    });
            },

            /**
             * log when a space is viewed to google analytics.
             * trigger after quote has been generated.
             */
            logGoogleLocationView() {
                googleLocationViewEvent({
                   name: this.space.title,
                   value: this.quote.value + this.quote.serviceFee,
                   price: this.quote.valueBeforeDiscount || this.quote.value,
                   id: this.space.id,
                   term: this.rental,
                });
            },
        },
    };
</script>

<style lang="less">
    @import "../../../../less/variables.less";
    @import (reference) "~assets/less/components/mixins/buttons";
    @import (reference) "../../../../../../node_modules/bootstrap/less/mixins/buttons";
    @import (reference) "../../../../../../node_modules/bootstrap/less/mixins/grid.less";
    @import (reference) "../../../../../../node_modules/bootstrap/less/mixins/clearfix.less";

    .tooltip {
        white-space: normal;
    }

    .availability-checker-widget {
        .trustpilot-widget {
            margin-top: 25px;
            margin-left: 20%;
            margin-right: 20%;
        }
    }

    .sticky-availability-form {
        @media (max-width: @screen-sm-max) {
            transform: translate3d(0, 0, 0) !important;
        }
    }

    .space-preview {
        padding: 0 20px;
    }

    .unavailable-buttons {
        .clearfix;
        padding: 20px 22px 12px;
        border-top: 1px solid #e2e2e2;
        margin-top: 20px;

        .button {
            .make-xs-column(6);

            &.full-width {
                .make-xs-column(12);
                padding-left: 0;
                padding-right: 0;

                &:first-of-type {
                    padding-right: 0;

                    @media (min-width: @screen-md-min) {
                        padding-right: 0;
                    }
                }

                &:last-of-type {
                    padding-left: 0;

                    @media (min-width: @screen-md-min) {
                        padding-left: 0;
                    }
                }
            }

            &:first-of-type {
                padding-left: 0;
                padding-right: 10px;

                @media (min-width: @screen-md-min) {
                    padding-right: 3px;
                }
            }

            &:last-of-type {
                padding-right: 0;
                padding-left: 10px;

                @media (min-width: @screen-md-min) {
                    padding-right: 3px;
                }
            }

            a.btn {
                width: 100%;
                height: 100%;
            }
        }
    }

    .unavailable-header.waiting-list {
        margin-bottom: 0;
    }

    .form-container {
        box-shadow: 0 0 12px rgba(0, 0, 0, 0.13);
        background: #ffffff;
        padding: 10px 0;
        border-radius: 6px;
    }

    .location-availability {
        .unavailable-header {
            border-bottom: 1px solid #e2e2e2;
            font-family: @headings-font-family;
            font-size: 14px;
            font-weight: 700;
            text-align: center;
        }

        .post-form {
            margin-top: 15px;
            text-align: center;
            font-size: 0.9em;

            a {
                font-weight: 600;
            }
        }

        h5 {
            text-transform: uppercase;
            font-size: 14px;
            letter-spacing: 0.7px;
        }

        .btn-group {
            font-size: 0;
            white-space: nowrap;
            width: 100%;

            > label {
                margin: 0;
                width: 50%;

                &:first-child {
                    .btn {
                        border-top-right-radius: 0;
                        border-bottom-right-radius: 0;
                    }
                }

                &:last-child {
                    .btn {
                        border-top-left-radius: 0;
                        border-bottom-left-radius: 0;
                    }
                }
            }

            .btn {
                float: none;
                display: block;
                position: relative;
                font-family: @headings-font-family;
                font-size: 13px;
                padding: 14px;

                input[type=radio] {
                    position: absolute;
                    opacity: 0;
                }
            }

            .btn-default {
                .button-variant(#414b4e, #ffffff, #c9d6e3);
            }

            &.btn-green {
                font-weight: 700;
                border-radius: 0 2px 2px 0;
            }
        }

        .availability-bookingtype {
            width: 100%;
            padding: 0 10px;
            margin-bottom: 5px;

            .form-group {
                width: 49%;
                display: inline-block;
                margin-bottom: 0;

                @media (min-width: @screen-md) {
                    display: table-cell;
                }
            }
        }

        .booking-form-alert {
            margin: 10px;
        }

        .availability-times {
            padding: 0 10px 20px;
            background-position: center 66px;
            background-repeat: no-repeat;
            border-bottom: 1px solid #e7e7e7;

            &[data-rental='long'] {
                padding: 12px 10px;

                .availability-arrival {
                    display: table;
                    width: 100%;

                    > * {
                        display: table-cell;
                        vertical-align: middle;
                        width: 50%;
                    }
                }

                h5 {
                    line-height: 40px;
                    white-space: nowrap;
                    padding-right: 20px;
                }
            }

            h4 {
                margin: 0 0 9px;
                font-size: 16px;
                font-weight: 700;
            }

            .form-group {
                margin-bottom: 7px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            .input-chevron-icon {
                background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAsAAAAHCAYAAADebrddAAAAiUlEQVQoU23PMQ4BURCA4W8TNXEXtUSHRlTWAZxBQUIhCldwADrREJVE7TLUChn2JVu8aWYm8/+ZmcI/GvhUdS795gXaOOOBZYbcoItRwC2c0MMWi5oQYPR3jAOOCOGIPnZYYY05bpjgleAQmpUwwBMdXFHiHUAdTsIBQ1wwTWAOTifNsI/V9Ye/3ZMXKBIawIoAAAAASUVORK5CYII=');
                background-position: center;
                background-repeat: no-repeat;
                position: absolute;
                top: 0;
                right: 10px;
                bottom: 0;
                margin: auto 0;
                width: 11px;
                height: 7px;
                z-index: 3;
                pointer-events: none;

                &+ input {
                    padding-right: 32px;
                }
            }

            > div {
                float: left;
                width: 100%;

                &:first-child {
                    padding-right: 0;
                    padding-left: 0;
                }
            }

            &:after {
                display: block;
                clear: both;
                content: '';
            }

            input {
                background-repeat: no-repeat;
                background-position: 7px center;
                background-size: 18px;
                width: 100%;

                &.flatpickr-input {
                    padding-left: 32px !important;
                    padding-right: 32px !important;
                    background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAAAXNSR0IArs4c6QAAATpJREFUaAXtWcENwjAMLIg3bMCjS7ITG7BGN4EFqO8RFFVWmjpu0oizFBUZzjnfpQlqh4FBBf5bgZOh/e8CY6kRlyiqd44r9fiZDbR2bc2BuxB8yJhkYK0u16ukfvnw/dYrasQR8JgTc4ODKUZBfWSEgq2u4AAum+MpiFakl/OCixqpLfAtiKuKqp+ECzdt2lQDUOFIoXJdu4mP1IDKhQ2oslRM0oGKYqtTXdSsnlR3Af2nLtmsXZBLyEXrgiJ0oEA8FygdcJGxoEj3Dmw5B6w6Ze3nUtx0znTvABuwrisvHB3wUtJahw5YlfPC0QEvJa11apzEphM2tyEuoVyl9vpd9w6k7gE8UI0f7ub+q9xDbHBRI+XAS0W0SZq4jMIVnUP5lgMcwMUU2iumWs1MwrjoFZOpY4KoQGcKzOpsqGC/nlsjAAAAAElFTkSuQmCC');
                }

                &.picker__input {
                    padding-left: 32px !important;
                    padding-right: 32px !important;
                    background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAAAXNSR0IArs4c6QAAA0tJREFUaAXtmb1qFVEUha+JP2CstFFJAraChYovENEulhYhRBB8gDxAIGClkCcIWNpY3VpsYiNIbARBkJDKBIJFGn/wd303d3DPzklmzjnjhMG7YWdm7zlrrb3PzGTOzO31Rjaagf97Bo411P64eK7Jb8qvyC8O/YK22Jb849DfavtC/kb+U36kNiX1x3IK/B3pYMDC0bqdleKK/Ks8tnA/Hg644GzF5qWyI/eF5MZwwh1lMffAaTGvyucOUNhW/l3AGX454Oc5ELCnyj2Qfw4cS05NCLkmD83ye+XvJjCDARviRAvNRmxMLH25F9pVblF+XJ5qYOGAy/P3lUM72x6KwZOvK3cpm/kvAVxweh20s2xGaP5XW+KXihs7vaY6OOG2WmhTQ5KdEWpTbgk3FJ+T/yuDGw2ruamYWqLtkRCW6Jvi6xEsFst+XUMDLYunliib1OgvckuyFMVQxsY0gAxaVptaqKm28WS0BB8Un6yN3hto8bENoIWm5aCmWnZKoz7JLXihFrI8yOJjG4AJTctBTdRWabMaYYE8YU9UovYPsBwpDaCJtuWhtpKFHhS3SyN6vWeKv7tcGyGaaFvztQWfdDcsQvvPXdxm6LV9bcFa/PU/HRxVnbSnPuUSQgFty0NtlfZLIwoQ+6lrnYKj2FYKBwag7espDQvdA3aJjfiPEiI9uJ8ARZsaCrO1Fbl922LG2LIWSTVuQs+V0gQ1WJ7KevwpS72E7knJixPHNFF5CYW6aeomhptic5qYFt7Ofq2b+JUD3VGcYzlNoG0boLaSjZWiveC1y91ycWz4RADecbk0C0N3VX61SByw9dq+tiBsVlnbdepSwpP7M7HsB7i41lLCYQZhU4u5EHfRxHLooMstKLYTyfVfazEHz4oDpyyn4QlZ1WUDJms5DcGkPPeFBp5UWxLQzn70Cw3Cua+UqcU38kqJeOdf6mliRu4fRJ35rEIDGB+X7PXI/rq8Ex+2VOfgpYfPfL6JXeUW5alrJUEHWDjg8vx95XjgNWITYlmTexHiI/24W2t9PZyCTn9eH/Yw2Mzr7448dDZycnDC3Yp1+icmO0NTCvihbkseO/tgwMKRbDH3wGEi4zrYyZ9ZD2tqdGw0A12YgT9yytMtwMwUhQAAAABJRU5ErkJggg==');
                }
            }
        }

        .form-control.form-control {
            height: 40px;
            background-color: #ffffff;
            border-color: #dbdbdb;
            &[disabled] {
                opacity: 0.75;
            }
        }

        .checker-detail-row .form-group {
            position: relative;
            margin-bottom: 0;

            input[type=text] {
                font-size: 12px;
                font-weight: 400;
                background-repeat: no-repeat;
                color: #4e535a;
                box-shadow: none;
                border-radius: 2px;
            }
        }

        .timepicker {
            border-top: 0;
        }

        .flatpickr-calendar {
            z-index: 40;
            top: 45px !important;
            left: 0 !important;
            right: auto !important;
            &:before,
            &:after {
                left: 50% !important;
                right: auto !important;
            }
        }
    }

    .error {
        padding: 0 10px;
    }
</style>
